import type { ChangeEvent, ClipboardEvent, ReactNode } from 'react';
import type {
  Key as ReactAriaKey,
  TagGroupProps as ReactAriaTagGroupProps,
  TagListProps as ReactAriaTagListProps,
  TagProps as ReactAriaTagProps,
} from 'react-aria-components';
import { Combobox, ComboboxInput, ComboboxOption, ComboboxOptions } from '@headlessui/react';
import { merge } from 'lodash-es';
import { useCallback, useMemo, useRef, useState } from 'react';
import {
  Button as ReactAriaButton,
  Tag as ReactAriaTag,
  TagGroup as ReactAriaTagGroup,
  TagList as ReactAriaTagList,
} from 'react-aria-components';
import { useDebounce } from 'use-debounce';

import type { DynamicIconName } from '../../assets/DynamicIcon/DynamicIcon';
import type { VariantProp } from '../../common/colors';
import type { MaxWidthTypes } from '../../utilities/shared/MaxWidth';
import type { MinWidthTypes } from '../../utilities/shared/MinWidth';
import type { WidthTypes } from '../../utilities/shared/Width';
import type { ControlSize } from '../shared/types';
import { DynamicIcon } from '../../assets/DynamicIcon/DynamicIcon';
import { fade, palette } from '../../common/colors';
import { contentStyles } from '../../common/menus';
import { zIndexes } from '../../common/z_indexes';
import { BaseInput } from '../../components/BaseInput/BaseInput';
import { colors, darkThemeSelector, fonts, shadows, styled } from '../../stitches.config';
import { BodySansSizes } from '../../text/Body';
import { LargeSansSizes } from '../../text/Large';
import { SmallSansSizes } from '../../text/Small';
import { AlignStack } from '../../utilities/AlignStack/AlignStack';
import { maxWidthCSS } from '../../utilities/shared/MaxWidth';
import { minWidthCSS } from '../../utilities/shared/MinWidth';
import { widthCSS } from '../../utilities/shared/Width';
import { Menu, MenuItem } from '../Menu/Menu';
import { useControlSize } from '../shared/context';
import { selectors, transitions } from '../shared/styles';
import { UtilityControl } from '../UtilityControl/UtilityControl';

const TokenInputTokenIcon = styled(DynamicIcon, {
  color: '$$iconColor',
});
TokenInputTokenIcon.displayName = 'TokenInputTokenIcon';

const TokenInputTokenValue = styled('span', {
  display: 'flex',
  color: '$$valueColor',
  wordBreak: 'break-word',
});
TokenInputTokenValue.displayName = 'TokenInputTokenValue';

const TokenInputTokenContent = styled(AlignStack);
TokenInputTokenContent.displayName = 'TokenInputTokenContent';

const TokenInputTokenRemove = styled(ReactAriaButton, {
  position: 'absolute',
  display: 'flex',
  opacity: '$$tokenInputTokenRemoveOpacity',

  [selectors.focus]: {
    outline: 'none',
  },

  variants: {
    size: {
      'x-small': {
        top: 0,
        right: 0,
      },
      small: {
        top: 0,
        right: 0,
      },
      medium: {
        top: '$2',
        right: '$2',
      },
      large: {
        top: '$4',
        right: '$4',
      },
      'x-large': {
        top: '$4',
        right: '$4',
      },
    },
  },
});
TokenInputTokenRemove.displayName = 'TokenInputTokenRemove';

const TokenInputTokenRemoveButton = styled(UtilityControl, {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  widthAll: '1lh',
  heightAll: '1lh',

  variants: {
    size: {
      'x-small': {
        borderRadius: '$4',
      },
      small: {
        borderRadius: '$4',
      },
      medium: {
        borderRadius: '$6',
      },
      large: {
        borderRadius: '$6',
      },
      'x-large': {
        borderRadius: '$6',
      },
    },
  },
});
TokenInputTokenRemoveButton.displayName = 'TokenInputTokenRemoveButton';

const TokenInputTokenContainer = styled(ReactAriaTag, {
  position: 'relative',
  alignItems: 'center',
  display: 'flex',
  flexDirection: 'row',
  color: '$$valueColor',
  fontFamily: fonts.sans,
  fontWeight: '$regular',
  transition: transitions.control,
  $$tokenInputTokenRemoveOpacity: 0,

  [selectors.hover]: {
    $$tokenInputTokenRemoveOpacity: 1,
  },

  [selectors.focus]: {
    outline: 'none',
    boxShadow: shadows.focusRingLight,
    $$tokenInputTokenRemoveOpacity: 1,

    [darkThemeSelector]: {
      boxShadow: shadows.focusRingDark,
    },
  },

  variants: {
    size: {
      'x-small': {
        padding: '0 $2',
        borderRadius: '$4',
        ...SmallSansSizes,
      },
      small: {
        padding: '0 $2',
        borderRadius: '$4',
        ...SmallSansSizes,
      },
      medium: {
        padding: '$2 $6',
        borderRadius: '$6',
        ...BodySansSizes,
      },
      large: {
        padding: '$4 $8',
        borderRadius: '$8',
        ...BodySansSizes,
      },
      'x-large': {
        padding: '$4 $8',
        borderRadius: '$8',
        ...LargeSansSizes,
      },
    },
    variant: {
      brand: {
        background: colors.tokenBgBrandLight,
        strokeAll: colors.tokenStrokeBrandLight,
        $$iconColor: colors.iconBrandLight,
        $$valueColor: colors.bodyBrandLight,
        $$removeColor: colors.iconBrandLight,

        [darkThemeSelector]: {
          background: colors.tokenBgBrandDark,
          strokeAll: colors.tokenStrokeBrandDark,
          $$iconColor: colors.iconBrandDark,
          $$valueColor: colors.bodyBrandDark,
          $$removeColor: colors.iconBrandDark,
        },

        [selectors.hover]: {
          $$iconColor: colors.bodyBrandLight,
          $$valueColor: colors.headingBrandLight,
          $$removeColor: colors.bodyBrandLight,

          [darkThemeSelector]: {
            $$iconColor: colors.bodyBrandDark,
            $$valueColor: colors.headingBrandDark,
            $$removeColor: colors.bodyBrandDark,
          },
        },
      },
      alternative: {
        background: colors.tokenBgAlternativeLight,
        strokeAll: colors.tokenStrokeAlternativeLight,
        $$iconColor: colors.iconAlternativeLight,
        $$valueColor: colors.bodyAlternativeLight,
        $$removeColor: colors.iconAlternativeLight,

        [darkThemeSelector]: {
          background: colors.tokenBgAlternativeDark,
          strokeAll: colors.tokenStrokeAlternativeDark,
          $$iconColor: colors.iconAlternativeDark,
          $$valueColor: colors.bodyAlternativeDark,
          $$removeColor: colors.iconAlternativeDark,
        },

        [selectors.hover]: {
          $$iconColor: colors.bodyAlternativeLight,
          $$valueColor: colors.headingAlternativeLight,
          $$removeColor: colors.bodyAlternativeLight,

          [darkThemeSelector]: {
            $$iconColor: colors.bodyAlternativeDark,
            $$valueColor: colors.headingAlternativeDark,
            $$removeColor: colors.bodyAlternativeDark,
          },
        },
      },
      attention: {
        background: colors.tokenBgAttentionLight,
        strokeAll: colors.tokenStrokeAttentionLight,
        $$iconColor: colors.iconAttentionLight,
        $$valueColor: colors.bodyAttentionLight,
        $$removeColor: colors.iconAttentionLight,

        [darkThemeSelector]: {
          background: colors.tokenBgAttentionDark,
          strokeAll: colors.tokenStrokeAttentionDark,
          $$iconColor: colors.iconAttentionDark,
          $$valueColor: colors.bodyAttentionDark,
          $$removeColor: colors.iconAttentionDark,
        },

        [selectors.hover]: {
          $$iconColor: colors.bodyAttentionLight,
          $$valueColor: colors.headingAttentionLight,
          $$removeColor: colors.bodyAttentionLight,

          [darkThemeSelector]: {
            $$iconColor: colors.bodyAttentionDark,
            $$valueColor: colors.headingAttentionDark,
            $$removeColor: colors.bodyAttentionDark,
          },
        },
      },
      negative: {
        background: colors.tokenBgNegativeLight,
        strokeAll: colors.tokenStrokeNegativeLight,
        $$iconColor: colors.iconNegativeLight,
        $$valueColor: colors.bodyNegativeLight,
        $$removeColor: colors.iconNegativeLight,

        [darkThemeSelector]: {
          background: colors.tokenBgNegativeDark,
          strokeAll: colors.tokenStrokeNegativeDark,
          $$iconColor: colors.iconNegativeDark,
          $$valueColor: colors.bodyNegativeDark,
          $$removeColor: colors.iconNegativeDark,
        },

        [selectors.hover]: {
          $$iconColor: colors.bodyNegativeLight,
          $$valueColor: colors.headingNegativeLight,
          $$removeColor: colors.bodyNegativeLight,

          [darkThemeSelector]: {
            $$iconColor: colors.bodyNegativeDark,
            $$valueColor: colors.headingNegativeDark,
            $$removeColor: colors.bodyNegativeDark,
          },
        },
      },
      neutral: {
        background: colors.tokenBgNeutralLight,
        strokeAll: colors.tokenStrokeNeutralLight,
        $$iconColor: colors.iconNeutralLight,
        $$valueColor: colors.bodyNeutralLight,
        $$removeColor: colors.iconNeutralLight,

        [darkThemeSelector]: {
          background: colors.tokenBgNeutralDark,
          strokeAll: colors.tokenStrokeNeutralDark,
          $$iconColor: colors.iconNeutralDark,
          $$valueColor: colors.bodyNeutralDark,
          $$removeColor: colors.iconNeutralDark,
        },

        [selectors.hover]: {
          $$iconColor: colors.bodyNeutralLight,
          $$valueColor: colors.headingNeutralLight,
          $$removeColor: colors.bodyNeutralLight,

          [darkThemeSelector]: {
            $$iconColor: colors.bodyNeutralDark,
            $$valueColor: colors.headingNeutralDark,
            $$removeColor: colors.bodyNeutralDark,
          },
        },
      },
      positive: {
        background: colors.tokenBgPositiveLight,
        strokeAll: colors.tokenStrokePositiveLight,
        $$iconColor: colors.iconPositiveLight,
        $$valueColor: colors.bodyPositiveLight,
        $$removeColor: colors.iconPositiveLight,

        [darkThemeSelector]: {
          background: colors.tokenBgPositiveDark,
          strokeAll: colors.tokenStrokePositiveDark,
          $$iconColor: colors.iconPositiveDark,
          $$valueColor: colors.bodyPositiveDark,
          $$removeColor: colors.iconPositiveDark,
        },

        [selectors.hover]: {
          $$iconColor: colors.bodyPositiveLight,
          $$valueColor: colors.headingPositiveLight,
          $$removeColor: colors.bodyPositiveLight,

          [darkThemeSelector]: {
            $$iconColor: colors.bodyPositiveDark,
            $$valueColor: colors.headingPositiveDark,
            $$removeColor: colors.bodyPositiveDark,
          },
        },
      },
    },
  },
});
TokenInputTokenContainer.displayName = 'TokenInputTokenContainer';

const getTokenInputTokenIconSize = (size: ControlSize) => {
  switch (size) {
    case 'x-small':
      return 12;
    case 'small':
      return 14;
    case 'medium':
      return 16;
    case 'large':
      return 16;
    case 'x-large':
      return 16;
    default:
      return 16;
  }
};

export type TokenInputTokenProps = Omit<ReactAriaTagProps, 'children'> & {
  /**
   * The contents of the token.
   */
  children?: ReactNode;
  /**
   * The annotation to display on the token.
   */
  annotation?: React.ReactNode;
  /**
   * The icon to display on the token.
   */
  icon?: DynamicIconName;
  /**
   * The size of the token.
   */
  size?: ControlSize;
  /**
   * The variant of the token.
   */
  variant?: VariantProp;
};

const getTokenGap = (size: ControlSize) => {
  switch (size) {
    case 'x-small':
    case 'small':
      return 2;
    case 'large':
    case 'x-large':
      return 6;
    default:
      return 4;
  }
};

const getTokenPreset = (size: ControlSize) => {
  switch (size) {
    case 'x-small':
    case 'small':
      return 'small';
    case 'x-large':
      return 'large';
    default:
      return 'body';
  }
};

function TokenInputToken({
  annotation,
  children,
  icon,
  size = 'medium',
  variant = 'brand',
  ...props
}: TokenInputTokenProps) {
  return (
    <TokenInputTokenContainer textValue={children} variant={variant} size={size} {...props}>
      <TokenInputTokenRemove slot="remove" size={size}>
        <TokenInputTokenRemoveButton icon="trash-can" size={size} variant="brand" label="Remove" />
      </TokenInputTokenRemove>
      <TokenInputTokenContent
        direction="row"
        align="center"
        gap={getTokenGap(size)}
        preset={getTokenPreset(size)}
        start={icon && <TokenInputTokenIcon icon={icon} size={getTokenInputTokenIconSize(size)} />}
        end={annotation && annotation}
        wrap="wrap"
      >
        <TokenInputTokenValue>{children}</TokenInputTokenValue>
      </TokenInputTokenContent>
    </TokenInputTokenContainer>
  );
}

const TokenInputTokensList = styled(ReactAriaTagList, {
  display: 'flex',
  flexWrap: 'wrap',
  outline: 'none',

  variants: {
    size: {
      'x-small': {
        gap: '$4',
        padding: '0 $2',
      },
      small: {
        gap: '$4',
        padding: '$2 $4',
      },
      medium: {
        gap: '$4',
        padding: '$2 $4',
      },
      large: {
        gap: '$6',
        padding: '$2 $4',
      },
      'x-large': {
        gap: '$8',
        padding: '$4 $6',
      },
    },
  },
});

const TokenInputTokensContainer = styled(ReactAriaTagGroup, {
  display: 'flex',
  alignItems: 'center',
});

interface TokenInputTokensProps<T>
  extends Omit<ReactAriaTagGroupProps, 'children'>,
    Pick<ReactAriaTagListProps<T>, 'items' | 'children' | 'renderEmptyState'> {
  size?: ControlSize;
  variant?: VariantProp;
}

function TokenInputTokens<T extends object>({
  items,
  children,
  renderEmptyState,
  size,
  ...props
}: TokenInputTokensProps<T>) {
  return (
    <TokenInputTokensContainer {...props}>
      <TokenInputTokensList items={items} renderEmptyState={renderEmptyState} size={size}>
        {children as ReactNode | ((item: object) => ReactNode)}
      </TokenInputTokensList>
    </TokenInputTokensContainer>
  );
}

const TokenInputField = styled(BaseInput, {
  display: 'flex',
  flex: 1,
  minWidth: '$120',
});
TokenInputField.displayName = 'TokenInputField';

const TokenInputContainer = styled('div', {
  position: 'relative',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  flexWrap: 'wrap',
  width: '100%',
  minWidth: 'min-content',
  height: 'min-content',
  background: colors.bgApplicationLight,
  strokeAll: fade(palette.controlStrokeBaseLight, 0.12),

  $$valueColor: colors.controlContentValueLight,
  $$placeholderColor: colors.controlContentPlaceholderLight,

  [selectors.focus]: {
    outline: 'none',
  },

  [selectors.focusWithin]: {
    boxShadow: shadows.focusRingLight,

    [darkThemeSelector]: {
      boxShadow: shadows.focusRingDark,
    },
  },

  [darkThemeSelector]: {
    background: colors.bgApplicationDark,
    strokeAll: fade(palette.controlStrokeBaseDark, 0.44),

    $$valueColor: colors.controlContentValueDark,
    $$placeholderColor: colors.controlContentPlaceholderDark,
  },

  '&[aria-disabled]': {
    background: colors.gray50,
    strokeAll: fade(palette.controlStrokeBaseLight, 0.3),
    opacity: 0.5,
    pointerEvents: 'none',

    [darkThemeSelector]: {
      background: colors.gray900,
      strokeAll: fade(palette.controlStrokeBaseDark, 0.36),
    },
  },

  variants: {
    isInvalid: {
      true: {
        strokeAllThick: colors.controlStrokeBaseErrorLight,

        [darkThemeSelector]: {
          strokeAll: colors.controlStrokeBaseErrorDark,
        },
      },
    },
    size: {
      'x-small': {
        minHeight: '$20',
        borderRadius: '$6',
      },
      small: {
        minHeight: '$24',
        borderRadius: '$8',
      },
      medium: {
        minHeight: '$28',
        borderRadius: '$8',
      },
      large: {
        minHeight: '$36',
        borderRadius: '$8',
      },
      'x-large': {
        minHeight: '$44',
        borderRadius: '$8',
      },
    },
  },
});
TokenInputContainer.displayName = 'TokenInputContainer';

const TokenInputMenuContainer = styled('div', contentStyles, {
  padding: '$4',
  zIndex: zIndexes.overlay,
  width: 'var(--input-width)',
  minWidth: '$180',
  maxWidth: '$420',

  // Prevent clicks from bubbling up to parent dropdowns
  '& *': {
    pointerEvents: 'auto',
  },

  // Ensure the container itself handles clicks properly
  '&': {
    pointerEvents: 'auto',
  },
});
TokenInputMenuContainer.displayName = 'TokenInputMenuContainer';

const TokenInputWrapper = styled('div');
TokenInputWrapper.displayName = 'TokenInputWrapper';

export type TokenOption = {
  id: string;
  name: string;
  display?: ReactNode;
};

type CommonTokenInputProps = {
  id?: string;
  options?: TokenOption[];
  inputProps?: Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'>;
  tagListProps?: { 'aria-label': string };
  getTokenProps?: (item: TokenOption) => TokenInputTokenProps;
  getTokenName?: (key: string) => string;
  width?: WidthTypes;
  maxWidth?: MaxWidthTypes;
  minWidth?: MinWidthTypes;
  creatable?: boolean | ((inputValue: string) => boolean);
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  invalid?: boolean;
  /**
   * The minimum number of characters required to be input before showing results.
   * To limit number of items rendered in very large lists.
   */
  minSearchLength?: number;
};

export type TokenInputProps = CommonTokenInputProps &
  // discriminated union for controlled/uncontrolled TokenInput value
  (| { value: string[]; onValueChange: (keys: Set<string>) => void; defaultValue?: string[] }
    | {
        defaultValue?: string[];
        onValueChange?: undefined;
        value?: undefined;
      }
  ) &
  // discriminated union for controlled/uncontrolled query value
  (| {
        /**
         * the value of the underlying text input. Used for a controlled TokenInput
         * */
        query: string;
        onQueryChange: (value: string) => void;
      }
    | {
        query?: undefined;
        onQueryChange?: undefined;
      }
  ) & {
    size?: ControlSize;
  };

type SingleTokenInputProps = CommonTokenInputProps &
  // discriminated union for controlled/uncontrolled TokenInput value
  (| { value: string; onValueChange: (key: string | undefined) => void; defaultValue?: string }
    | {
        defaultValue?: string;
        onValueChange?: undefined;
        value?: undefined;
      }
  ) &
  // discriminated union for controlled/uncontrolled query value
  (| {
        /**
         * the value of the underlying text input. Used for a controlled TokenInput
         * */
        query: string;
        onQueryChange: (value: string) => void;
      }
    | {
        query?: undefined;
        onQueryChange?: undefined;
      }
  ) & {
    size?: ControlSize;
  };

export function TokenInput({
  id = 'tokenInput',
  size = 'medium',
  onValueChange,
  onQueryChange,
  query,
  options,
  creatable,
  inputProps,
  tagListProps,
  getTokenProps = () => ({}),
  getTokenName,
  defaultValue: defaultValueProp,
  value,
  width,
  maxWidth,
  minWidth,
  onKeyDown,
  invalid,
  minSearchLength,
}: TokenInputProps) {
  const isControlled = onQueryChange != null;
  const controlSize = useControlSize(size, 'medium');

  const optionsByKey = useMemo(
    () => new Map(options?.map((option) => [option.id, option])),
    [options],
  );

  let defaultValue: TokenOption[] = [];
  if (defaultValueProp) {
    defaultValue = [
      ...defaultValueProp.map((key) => ({
        id: key,
        name: getTokenName?.(key) ?? optionsByKey?.get(key)?.name ?? key,
      })),
    ];
  }
  if (value) {
    defaultValue = Array.from(value).map((key) => ({
      id: key,
      name: getTokenName?.(key) ?? optionsByKey?.get(key)?.name ?? key,
    }));
  }
  const [internalTokens, setInternalTokens] =
    useState<{ id: string; name: string }[]>(defaultValue);

  const tokens = value
    ? value.map((key) => ({
        id: key,
        name: getTokenName?.(key) ?? optionsByKey?.get(key)?.name ?? key,
      }))
    : internalTokens;
  const inputRef = useRef<HTMLInputElement>(null);

  const [internalQuery, setInternalQuery] = useState(query ?? '');
  const [debouncedInternalQuery] = useDebounce(query ?? internalQuery, 100);

  const handleOnChange = useCallback(
    (values: { id: string; name: string }[] | null) => {
      if (values) {
        setInternalTokens(values);
        if (onValueChange) {
          onValueChange(new Set(values.map((token) => token.id)));
        }
        setInternalQuery('');
        if (onQueryChange) {
          onQueryChange('');
        }
      }
    },
    [setInternalTokens, onValueChange, onQueryChange],
  );

  const onRemove = useCallback(
    (keys: Set<ReactAriaKey>) => {
      setInternalTokens((prevTokens) =>
        Array.from(prevTokens).filter((item) => !keys.has(item.id)),
      );
      const newVal = new Set(
        tokens.map((token) => token.id).filter((tokenId) => !keys.has(tokenId)),
      );
      if (onValueChange) {
        onValueChange(newVal);
      }
      if (newVal.size === 0 && inputRef?.current) {
        inputRef.current.focus();
      }
    },
    [tokens, onValueChange],
  );

  const handleOnClose = useCallback(() => {
    setInternalQuery('');
    if (onQueryChange) {
      onQueryChange('');
    }
  }, [setInternalQuery, onQueryChange]);

  const handleQueryChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const val = e.currentTarget.value;
      if (onQueryChange) {
        onQueryChange(val);
      }
      setInternalQuery(val);
    },
    [onQueryChange],
  );

  const handlePaste = useCallback(
    (e: ClipboardEvent<HTMLInputElement>) => {
      if (e.target && 'value' in e.target && typeof e.target.value === 'string') {
        const val = e.target.value;
        if (onQueryChange) {
          onQueryChange(val);
        }
        setInternalQuery(val);
      }
    },
    [onQueryChange],
  );

  const filteredOptions = useMemo(() => {
    if (isControlled || !debouncedInternalQuery) return options;

    const lowerCaseQuery = debouncedInternalQuery.toLowerCase();

    return (
      options
        ?.filter((option) => option.name.toLowerCase().includes(lowerCaseQuery))
        // Only show up to 100 options
        .slice(0, 100)
    );
  }, [isControlled, options, debouncedInternalQuery]);

  const canCreateQuery = useMemo(
    (): boolean =>
      typeof creatable === 'function' ? creatable(debouncedInternalQuery) : !!creatable,
    [creatable, debouncedInternalQuery],
  );

  return (
    <Combobox
      as={TokenInputWrapper}
      value={tokens ?? null}
      onChange={handleOnChange}
      onClose={handleOnClose}
      onKeyDown={onKeyDown}
      multiple
      immediate
      css={merge(widthCSS(width), maxWidthCSS(maxWidth), minWidthCSS(minWidth))}
    >
      <TokenInputContainer size={controlSize} isInvalid={invalid}>
        {tokens.length > 0 && (
          <TokenInputTokens {...tagListProps} items={tokens} onRemove={onRemove} size={size}>
            {(item) => (
              <TokenInputToken {...getTokenProps(item)} key={item.id} size={controlSize}>
                {item.name}
              </TokenInputToken>
            )}
          </TokenInputTokens>
        )}
        <TokenInputField
          size={controlSize}
          isNested
          shouldApplyFocusStyles={false}
          inputRef={inputRef}
          inputProps={{
            ...inputProps,
            placeholder: inputProps?.placeholder ?? 'Select...',
            as: ComboboxInput,
            value: query ?? internalQuery,
            onChange: handleQueryChange,
            onPaste: handlePaste,
          }}
        />
      </TokenInputContainer>
      {((filteredOptions && filteredOptions?.length > 0) ||
        (creatable && canCreateQuery && debouncedInternalQuery)) && (
        <ComboboxOptions
          anchor={{
            to: 'bottom',
            gap: 4,
            padding: 12,
          }}
          className={`disableOutsideClick_${id}`}
          as={TokenInputMenuContainer}
        >
          <Menu>
            {creatable && canCreateQuery && debouncedInternalQuery && (
              <ComboboxOption value={{ id: debouncedInternalQuery, name: debouncedInternalQuery }}>
                {({ focus, selected }) => (
                  <MenuItem active={focus} enabled={selected}>
                    Create "{debouncedInternalQuery}"
                  </MenuItem>
                )}
              </ComboboxOption>
            )}
            {minSearchLength && debouncedInternalQuery.length < minSearchLength ? (
              <ComboboxOption value={{ id: '___', name: '___' }}>
                <MenuItem disabled>
                  Please provide at least {minSearchLength}{' '}
                  {minSearchLength === 1 ? 'character' : 'characters'} to see results.
                </MenuItem>
              </ComboboxOption>
            ) : (
              filteredOptions?.map((token) => (
                <ComboboxOption key={token.id} value={token}>
                  {({ focus, selected }) => (
                    <MenuItem active={focus} enabled={selected}>
                      {token.display ?? token.name}
                    </MenuItem>
                  )}
                </ComboboxOption>
              ))
            )}
          </Menu>
        </ComboboxOptions>
      )}
    </Combobox>
  );
}

const SingleTokenInputTokensContainer = styled(TokenInputTokensContainer, {
  width: '100%',
});
SingleTokenInputTokensContainer.displayName = 'SingleTokenInputTokensContainer';

const SingleTokenInputTokensList = styled(TokenInputTokensList, {
  width: '100%',
});
SingleTokenInputTokensList.displayName = 'SingleTokenInputTokensList';

function SingleTokenInputTokens<T extends object>({
  items,
  children,
  renderEmptyState,
  size,
  ...props
}: TokenInputTokensProps<T>) {
  return (
    <SingleTokenInputTokensContainer {...props}>
      <SingleTokenInputTokensList items={items} renderEmptyState={renderEmptyState} size={size}>
        {children as ReactNode}
      </SingleTokenInputTokensList>
    </SingleTokenInputTokensContainer>
  );
}

const SingleTokenInputToken = styled(TokenInputToken, {
  padding: '$8',
});
SingleTokenInputToken.displayName = 'SingleTokenInputToken';

export function SingleTokenInput({
  id = 'tokenInput',
  size = 'medium',
  onValueChange,
  onQueryChange,
  query,
  options,
  creatable = false,
  inputProps,
  tagListProps,
  getTokenProps = () => ({}),
  getTokenName,
  defaultValue: defaultValueProp,
  value,
  width,
  maxWidth,
  minWidth,
  onKeyDown,
  minSearchLength,
}: SingleTokenInputProps) {
  const isControlled = onQueryChange != null;
  const controlSize = useControlSize(size, 'medium');

  let defaultValue: TokenOption | undefined;
  if (defaultValueProp) {
    defaultValue = {
      id: defaultValueProp,
      name: getTokenName ? getTokenName(defaultValueProp) : defaultValueProp,
    };
  }
  if (value) {
    defaultValue = {
      id: value,
      name: getTokenName ? getTokenName(value) : value,
    };
  }
  const [internalToken, setInternalToken] = useState<{ id: string; name: string } | undefined>(
    defaultValue,
  );
  const token: TokenOption | undefined = useMemo(
    () =>
      value
        ? options?.find((opt) => opt.id === value) ?? {
            id: value,
            name: getTokenName ? getTokenName(value) : value,
          }
        : internalToken,
    [value, options, internalToken, getTokenName],
  );
  const inputRef = useRef<HTMLInputElement>(null);

  const [internalQuery, setInternalQuery] = useState(query ?? '');
  const [debouncedInternalQuery] = useDebounce(internalQuery, 100);

  const handleOnChange = useCallback(
    (newValue: { id: string; name: string } | null) => {
      if (newValue) {
        setInternalToken(newValue);
        onValueChange?.(newValue.id);
        setInternalQuery('');
        onQueryChange?.('');
      }
    },
    [setInternalToken, onValueChange, onQueryChange],
  );

  const onRemove = useCallback(() => {
    setInternalToken(undefined);
    onValueChange?.(undefined);
    inputRef.current?.focus();
  }, [onValueChange]);

  const handleOnClose = useCallback(() => {
    setInternalQuery('');
    onQueryChange?.('');
  }, [setInternalQuery, onQueryChange]);

  const handleQueryChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const val = e.currentTarget.value;
      if (onQueryChange) {
        onQueryChange(val);
      }
      setInternalQuery(val);
    },
    [onQueryChange],
  );

  const filteredOptions = useMemo(() => {
    if (isControlled || !debouncedInternalQuery) return options;

    const lowerCaseQuery = debouncedInternalQuery.toLowerCase();

    return (
      options
        ?.filter((option) => option.name.toLowerCase().includes(lowerCaseQuery))
        // Only show up to 100 options at once
        .slice(0, 100)
    );
  }, [isControlled, options, debouncedInternalQuery]);

  return (
    <Combobox
      as={TokenInputWrapper}
      value={token ?? null}
      onChange={handleOnChange}
      onClose={handleOnClose}
      onKeyDown={onKeyDown}
      immediate
      css={merge(widthCSS(width), maxWidthCSS(maxWidth), minWidthCSS(minWidth))}
    >
      <TokenInputContainer size={controlSize}>
        {token ? (
          <SingleTokenInputTokens {...tagListProps} items={[token]} onRemove={onRemove} size={size}>
            <SingleTokenInputToken {...getTokenProps(token)} key={token.id} size={controlSize}>
              {token.display ?? token.name}
            </SingleTokenInputToken>
          </SingleTokenInputTokens>
        ) : (
          <TokenInputField
            size={controlSize}
            isNested
            shouldApplyFocusStyles={false}
            inputRef={inputRef}
            inputProps={{
              ...inputProps,
              placeholder: inputProps?.placeholder ?? 'Select...',
              as: ComboboxInput,
              value: query ?? internalQuery,
              onChange: handleQueryChange,
            }}
          />
        )}
      </TokenInputContainer>
      {((filteredOptions && filteredOptions?.length > 0) || (creatable && internalQuery)) && (
        <ComboboxOptions
          anchor={{
            gap: 4,
            padding: 12,
          }}
          className={`disableOutsideClick_${id}`}
          as={TokenInputMenuContainer}
        >
          <Menu>
            {!filteredOptions?.length && creatable && internalQuery && (
              <ComboboxOption value={{ id: internalQuery, name: internalQuery }}>
                {({ focus, selected }) => (
                  <MenuItem active={focus} enabled={selected}>
                    Create "{internalQuery}"
                  </MenuItem>
                )}
              </ComboboxOption>
            )}
            {minSearchLength && debouncedInternalQuery.length < minSearchLength ? (
              <ComboboxOption value={{ id: '___', name: '___' }}>
                <MenuItem disabled>
                  Please provide at least {minSearchLength}{' '}
                  {minSearchLength === 1 ? 'character' : 'characters'} to see results.
                </MenuItem>
              </ComboboxOption>
            ) : (
              filteredOptions?.map((option) => (
                <ComboboxOption key={option.id} value={option}>
                  {({ focus, selected }) => (
                    <MenuItem active={focus} enabled={selected}>
                      {option.display ?? option.name}
                    </MenuItem>
                  )}
                </ComboboxOption>
              ))
            )}
          </Menu>
        </ComboboxOptions>
      )}
    </Combobox>
  );
}
